import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSnapshot } from 'valtio';
import OverlayModal from '@components/shared/OverlayModal';
import ToastIcon from '@components/shared/ToastIcon';
import { notificationsStore, removeActiveToast, removeUploadToast, clearAllActiveToasts, clearAllUploadToasts } from '@/store/notifications-store';
import { store } from '@/store/valtio-store';
import cx from 'classnames';

/**
 * Toast Container Component
 * Displays notification toasts, upload progress, and sync status
 * Uses semantic HTML with proper accessibility
 */
export default function ToastContainer() {
    // Only render on client to avoid SSR hydration mismatch and useLayoutEffect warnings
    const [isClient, setIsClient] = useState(false);
    
    useEffect(() => {
        setIsClient(true);
    }, []);
    
    // Don't render or use hooks that depend on useLayoutEffect until client-side
    if (!isClient) {
        return null;
    }
    
    return <ToastContainerClient />;
}

/**
 * Client-only Toast Container - uses valtio hooks that require useLayoutEffect
 */
function ToastContainerClient() {
    const notifSnap = useSnapshot(notificationsStore);
    const storeSnap = useSnapshot(store);
    
    const pendingRequestCount = Object.keys(storeSnap.pendingRequests).length;
    const activeToasts = notifSnap.activeToasts;
    const events = notifSnap.events;
    const uploadToasts = notifSnap.uploadToasts;
    const confirmationToast = notifSnap.confirmationToast;
    
    // State for error details modal
    const [errorModalEvent, setErrorModalEvent] = useState(null);
    
    // Get event details for active toasts
    const toastEvents = activeToasts
        .map(id => events.find(e => e.id === id))
        .filter(Boolean);
    
    // Check if there are dismissible toasts (notification toasts or upload toasts)
    // Note: Sync status toasts are not dismissible as they represent active requests
    const hasDismissibleToasts = uploadToasts.length > 0 || toastEvents.length > 0;
    
    // Handle close all toasts
    const handleCloseAll = useCallback(() => {
        clearAllActiveToasts();
        clearAllUploadToasts();
    }, []);
    
    return (
        <>
            {/* Confirmation Toast - Blocking overlay */}
            {confirmationToast && (
                <ConfirmationToast 
                    confirmation={confirmationToast}
                />
            )}
            
            {/* Error Details Modal */}
            {errorModalEvent && (
                <ErrorDetailsModal
                    event={errorModalEvent}
                    onClose={() => setErrorModalEvent(null)}
                />
            )}

            {/* Stack toasts into #toast-stack-main (ToastStack) */}
            {typeof document !== 'undefined' && (() => {
                const target = document.getElementById('toast-stack-main');
                return target && createPortal(
                    <div
                        className="toast-container flex flex-col gap-1.5 sm:gap-2 w-full pointer-events-none"
                        role="region"
                        aria-label="Notifications"
                    >
                        {hasDismissibleToasts && (
                            <button
                                type="button"
                                onClick={handleCloseAll}
                                className="toast-btn toast-btn--link pointer-events-auto"
                                title="Close all"
                            >
                                <span>Close all</span>
                            </button>
                        )}
                        {pendingRequestCount > 0 && (
                            <SyncStatusToast count={pendingRequestCount} />
                        )}
                        {uploadToasts.map(upload => (
                            <UploadToast
                                key={upload.id}
                                upload={upload}
                                onDismiss={() => removeUploadToast(upload.id)}
                            />
                        ))}
                        {toastEvents.map(event => (
                            <NotificationToast
                                key={event.id}
                                event={event}
                                onDismiss={() => removeActiveToast(event.id)}
                                onShowDetails={(errorEvent) => setErrorModalEvent(errorEvent)}
                            />
                        ))}
                    </div>,
                    target
                );
            })()}
        </>
    );
}

/**
 * Sync Status Toast - Shows pending request count
 */
function SyncStatusToast({ count }) {
    return (
        <div
            className="toast toast-card is-accent pointer-events-auto"
            role="status"
            aria-live="polite"
            title={`Syncing ${count} ${count === 1 ? 'request' : 'requests'}`}
        >
            <ToastIcon spin />
            <p className="toast-card__text">
                Syncing <strong>{count}</strong> {count === 1 ? 'request' : 'requests'}
            </p>
        </div>
    );
}

/**
 * Upload Toast - Shows upload progress
 */
function UploadToast({ upload, onDismiss }) {
    const { file, progress, status, error } = upload;
    const [isVisible, setIsVisible] = useState(false);
    
    // Calculate duration based on message length, minimum 2 seconds
    const fileName = file?.name || 'Uploading file';
    const duration = Math.max(2000, fileName.length * 50);
    
    // Trigger enter animation on mount
    useEffect(() => {
        requestAnimationFrame(() => {
            setIsVisible(true);
        });
    }, []);
    
    // Handle manual dismissal with transition
    const handleDismiss = () => {
        setIsVisible(false);
        setTimeout(onDismiss, 200);
    };
    
    // Auto-dismiss successful uploads after calculated duration (minimum 2s)
    useEffect(() => {
        if (status === 'success') {
            const timer = setTimeout(() => {
                setIsVisible(false);
                setTimeout(onDismiss, 200);
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [status, duration, onDismiss]);
    
    const isError = status === 'error';
    const isSuccess = status === 'success';
    const isUploading = status === 'uploading';
    
    return (
        <div
            className={cx(
                'toast toast-card pointer-events-auto transition-all duration-200',
                {
                    'is-accent': isUploading,
                    'is-success': isSuccess,
                    'is-error': isError,
                    'opacity-0 -translate-x-full': !isVisible,
                    'opacity-100 translate-x-0': isVisible,
                }
            )}
            role="status"
            aria-live="polite"
            title={isError && error ? `${fileName}: ${error}` : fileName}
        >
            {isUploading && <ToastIcon spin />}
            {isSuccess && <ToastIcon name="check" />}
            {isError && <ToastIcon name="alert" />}
            <p className="toast-card__text">
                {fileName}
                {isError && error && <small> · {error}</small>}
            </p>
            {isUploading && (
                <div className="toast-progress" aria-hidden="true">
                    <span style={{ width: `${progress}%` }} />
                </div>
            )}
            <button
                type="button"
                onClick={handleDismiss}
                className="toast-btn toast-btn--icon toast-btn--small"
                aria-label="Dismiss"
            >
                <ToastIcon name="close" className="" />
            </button>
        </div>
    );
}

/**
 * Notification Toast - Shows info/success/error/fatal messages
 */
function NotificationToast({ event, onDismiss, onShowDetails }) {
    const { level, message, details } = event;
    const [isVisible, setIsVisible] = useState(false);
    
    // Calculate duration based on message length, minimum 2 seconds
    const duration = Math.max(2000, message.length * 50);
    
    // Trigger enter animation on mount
    useEffect(() => {
        requestAnimationFrame(() => {
            setIsVisible(true);
        });
    }, []);
    
    // Handle manual dismissal with transition
    const handleDismiss = (e) => {
        e.stopPropagation(); // Prevent triggering click handler on the toast
        setIsVisible(false);
        setTimeout(onDismiss, 200);
    };
    
    // Handle click on toast to show details (only for errors with details)
    const hasDetails = details && (level === 'error' || level === 'fatal');
    const handleToastClick = () => {
        if (hasDetails && onShowDetails) {
            onShowDetails(event);
        }
    };
    
    // Auto-dismiss info and success toasts after calculated duration (minimum 2s)
    useEffect(() => {
        if (level === 'info' || level === 'success') {
            const timer = setTimeout(() => {
                setIsVisible(false);
                setTimeout(onDismiss, 200);
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [level, duration, onDismiss]);
    
    const isInfo = level === 'info';
    const isSuccess = level === 'success';
    const isError = level === 'error';
    const isFatal = level === 'fatal';
    
    return (
        <div
            className={cx(
                'toast toast-card pointer-events-auto transition-all duration-200',
                {
                    'is-accent': isInfo,
                    'is-success': isSuccess,
                    'is-error': isError || isFatal,
                    'is-clickable': hasDetails,
                    'opacity-0 -translate-x-full': !isVisible,
                    'opacity-100 translate-x-0': isVisible,
                }
            )}
            role={isInfo || isSuccess ? 'status' : 'alert'}
            aria-live={isInfo || isSuccess ? 'polite' : 'assertive'}
            onClick={hasDetails ? handleToastClick : undefined}
            title={message}
        >
            <ToastIcon name={isInfo ? 'info' : isSuccess ? 'check' : isFatal ? 'warn' : 'alert'} />
            <div className="min-w-0 flex-1">
                <p className={cx('toast-card__text toast-card__text--wrap', { 'text-base': event.largeText })}>{message}</p>
                {hasDetails && (
                    <p className="toast-card__text toast-card__muted">Click for details</p>
                )}
            </div>
            <button
                type="button"
                onClick={handleDismiss}
                className="toast-btn toast-btn--icon toast-btn--small"
                aria-label="Dismiss"
            >
                <ToastIcon name="close" className="" />
            </button>
        </div>
    );
}

/**
 * Error Details Modal - Shows full error stack and details
 */
function ErrorDetailsModal({ event, onClose }) {
    const { message, details, timestamp } = event;
    const [isVisible, setIsVisible] = useState(false);
    const [copied, setCopied] = useState(false);
    
    // Trigger enter animation on mount
    useEffect(() => {
        requestAnimationFrame(() => {
            setIsVisible(true);
        });
    }, []);
    
    const handleClose = useCallback(() => {
        setIsVisible(false);
        setTimeout(onClose, 200);
    }, [onClose]);
    
    // Format error details for display
    const formatErrorDetails = (details) => {
        if (!details) return null;
        
        const parts = [];
        
        // Error message/string
        if (details.error) {
            parts.push({ label: 'Error', value: String(details.error) });
        }
        
        // Stack trace
        if (details.stack) {
            parts.push({ label: 'Stack Trace', value: String(details.stack) });
        }
        
        // Source file
        if (details.source) {
            parts.push({ label: 'Source', value: String(details.source) });
        }
        
        // Line and column numbers
        if (details.lineno !== undefined || details.colno !== undefined) {
            const location = [];
            if (details.lineno !== undefined) location.push(`Line ${details.lineno}`);
            if (details.colno !== undefined) location.push(`Column ${details.colno}`);
            parts.push({ label: 'Location', value: location.join(', ') });
        }
        
        // Reason (for promise rejections)
        if (details.reason) {
            const reasonStr = typeof details.reason === 'object' 
                ? JSON.stringify(details.reason, null, 2)
                : String(details.reason);
            parts.push({ label: 'Reason', value: reasonStr });
        }
        
        // Args (for console errors)
        if (details.args && Array.isArray(details.args)) {
            const argsStr = details.args
                .map(arg => {
                    if (typeof arg === 'object') {
                        try {
                            return JSON.stringify(arg, null, 2);
                        } catch {
                            return String(arg);
                        }
                    }
                    return String(arg);
                })
                .join('\n');
            parts.push({ label: 'Arguments', value: argsStr });
        }
        
        // Timestamp
        if (details.timestamp) {
            parts.push({ label: 'Timestamp', value: new Date(details.timestamp).toLocaleString('en-GB') });
        }
        
        // Any other properties
        const knownKeys = ['error', 'stack', 'source', 'lineno', 'colno', 'reason', 'args', 'timestamp'];
        Object.keys(details).forEach(key => {
            if (!knownKeys.includes(key) && details[key] !== undefined && details[key] !== null) {
                const value = typeof details[key] === 'object'
                    ? JSON.stringify(details[key], null, 2)
                    : String(details[key]);
                parts.push({ label: key.charAt(0).toUpperCase() + key.slice(1), value });
            }
        });
        
        return parts;
    };
    
    const formattedDetails = formatErrorDetails(details);
    
    // Format all error details as a single copyable text string
    const errorTextForCopy = useMemo(() => {
        const lines = [];
        
        lines.push('='.repeat(60));
        lines.push('ERROR DETAILS');
        lines.push('='.repeat(60));
        lines.push('');
        
        lines.push(`Message: ${message}`);
        lines.push('');
        
        if (timestamp) {
            lines.push(`Timestamp: ${new Date(timestamp).toISOString()}`);
            lines.push(`Time (Local): ${new Date(timestamp).toLocaleString('en-GB')}`);
            lines.push('');
        }
        
        if (formattedDetails && formattedDetails.length > 0) {
            formattedDetails.forEach((part) => {
                lines.push('─'.repeat(60));
                lines.push(`${part.label}:`);
                lines.push('─'.repeat(60));
                lines.push(part.value);
                lines.push('');
            });
        }
        
        // Include raw details as JSON if available
        if (details) {
            lines.push('─'.repeat(60));
            lines.push('Raw Details (JSON):');
            lines.push('─'.repeat(60));
            try {
                lines.push(JSON.stringify(details, null, 2));
            } catch (e) {
                lines.push(String(details));
            }
            lines.push('');
        }
        
        lines.push('='.repeat(60));
        
        return lines.join('\n');
    }, [message, timestamp, formattedDetails, details]);
    
    // Copy all error details to clipboard
    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(errorTextForCopy);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = errorTextForCopy;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (e) {
                console.error('Failed to copy:', e);
            }
            document.body.removeChild(textArea);
        }
    }, [errorTextForCopy]);
    
    const headerDescription = (
        <>
            <p className="text-sm text-gray-600 m-0 break-words">{message}</p>
            {timestamp ? (
                <p className="text-xs text-gray-500 mt-1 m-0">
                    {new Date(timestamp).toLocaleString('en-GB')}
                </p>
            ) : null}
        </>
    );

    return (
        <OverlayModal
            isOpen={isVisible}
            onClose={handleClose}
            size="medium"
            zIndex={9999}
            maxWidthClass="max-w-4xl"
            title="Error Details"
            description={headerDescription}
            overlayClassName={cx('transition-opacity duration-200', {
                'opacity-0': !isVisible,
                'opacity-100': isVisible,
            })}
            footer={(
                <>
                    <button
                        type="button"
                        onClick={handleCopy}
                        className={cx(
                            'mr-auto px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center gap-2',
                            copied
                                ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                                : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
                        )}
                    >
                        {copied ? (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Copied!
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Copy All
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-white bg-gray-700 rounded-md hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                        Close
                    </button>
                </>
            )}
            footerClassName="!justify-between"
        >
            <div className="mb-4">
                <textarea
                    readOnly
                    value={errorTextForCopy}
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded text-xs font-mono text-gray-800 whitespace-pre-wrap break-words resize-none"
                    style={{ minHeight: '300px', fontFamily: 'monospace' }}
                    onClick={(e) => {
                        if (window.innerWidth <= 768) {
                            e.target.select();
                        }
                    }}
                />
            </div>
            {formattedDetails && formattedDetails.length > 0 && (
                <div className="space-y-6">
                    {formattedDetails.map((part, index) => (
                        <div key={index}>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                                {part.label}
                            </h4>
                            <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-4 overflow-x-auto whitespace-pre-wrap break-words font-mono text-gray-800 select-all">
                                {part.value}
                            </pre>
                        </div>
                    ))}
                </div>
            )}
        </OverlayModal>
    );
}

/**
 * Confirmation Toast - Blocking confirmation dialog
 */
function ConfirmationToast({ confirmation }) {
    const { message, onConfirm, onCancel } = confirmation;
    const [isVisible, setIsVisible] = useState(false);
    
    // Trigger enter animation on mount
    useEffect(() => {
        requestAnimationFrame(() => {
            setIsVisible(true);
        });
    }, []);
    
    const handleConfirm = () => {
        setIsVisible(false);
        setTimeout(() => {
            onConfirm();
        }, 200);
    };
    
    const handleCancel = () => {
        setIsVisible(false);
        setTimeout(() => {
            onCancel();
        }, 200);
    };
    
    return (
        <OverlayModal
            isOpen={isVisible}
            onClose={handleCancel}
            size="small"
            zIndex={9999}
            title="Confirm"
            description={message}
            overlayClassName={cx('transition-opacity duration-200', {
                'opacity-0': !isVisible,
                'opacity-100': isVisible,
            })}
            footer={(
                <>
                    <button
                        type="button"
                        onClick={handleCancel}
                        aria-label="Cancel"
                        title="Cancel"
                        className="p-2 sm:px-4 sm:py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                        <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span className="hidden sm:inline text-sm font-medium">Cancel</span>
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        aria-label="Confirm"
                        title="Confirm"
                        className="p-2 sm:px-4 sm:py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                        <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="hidden sm:inline text-sm font-medium">Confirm</span>
                    </button>
                </>
            )}
        >
            <div className="flex-shrink-0 mt-0.5 mb-2">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            </div>
        </OverlayModal>
    );
}
